import React, { useEffect } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { X, Download, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { saveFile } from '../../native/files';
import { openExternal } from '../../native/links';

// The viewer is dark in both themes. Its colors are ones the light-mode remaps in
// index.css never touch (white with an alpha other than 5 or 10), so the buttons
// stay light on dark everywhere, visible without hover (task b512992b).
const BAR_BUTTON = 'p-2 rounded-lg text-white/90 bg-white/15 ring-1 ring-inset ring-white/20 hover:bg-white/25';

const GOOGLE_VIEWER_TYPES = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

export default function AttachmentPreviewModal({ attachment, attachments = [], onClose, onNavigate }) {
  const { t } = useTranslation();
  const currentIdx = attachments.findIndex(a => a.id === attachment?.id);
  const hasPrev = currentIdx > 0;
  const hasNext = currentIdx < attachments.length - 1;

  // Arrow key navigation + Escape (capture phase so it runs before
  // Radix/task-detail handlers - Escape must close only the preview,
  // never the task modal underneath)
  useEffect(() => {
    if (!attachment) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft' && hasPrev) {
        e.stopImmediatePropagation();
        onNavigate(attachments[currentIdx - 1]);
      } else if (e.key === 'ArrowRight' && hasNext) {
        e.stopImmediatePropagation();
        onNavigate(attachments[currentIdx + 1]);
      } else if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [attachment, attachments, hasPrev, hasNext, onNavigate, onClose, currentIdx]);

  if (!attachment) return null;

  const { url, filename, content_type } = attachment;
  const ext = (filename || '').split('.').pop().toLowerCase();

  const isImage  = content_type?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
  const isPdf    = content_type === 'application/pdf' || ext === 'pdf';
  const isOffice = GOOGLE_VIEWER_TYPES.includes(content_type) || ['doc', 'docx', 'xls', 'xlsx'].includes(ext);
  const googleUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;

  return (
    <DialogPrimitive.Root open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogPrimitive.Portal>
        {/* Full-screen dark overlay */}
        <DialogPrimitive.Overlay className="fixed inset-0 z-[9999] bg-black/95" />

        {/* Content - occupies the entire viewport */}
        <DialogPrimitive.Content
          className="bb-always-dark fixed inset-0 z-[9999] flex flex-col outline-none"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onEscapeKeyDown={onClose}
          // Content IS the full viewport - nothing is "outside" it
          onInteractOutside={(e) => e.preventDefault()}
        >
          {/* Accessibility */}
          <DialogPrimitive.Title className="sr-only">{filename}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">{t('ui_common.attachment.preview')}</DialogPrimitive.Description>

          {/* ── Header bar ────────────────────────────────────────── */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0 flex-1 mr-4">
              {attachments.length > 1 && (
                <span className="text-xs text-white/50 flex-shrink-0">
                  {currentIdx + 1} / {attachments.length}
                </span>
              )}
              <span className="text-sm text-white/80 truncate">{filename}</span>
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
              {hasPrev && (
                <button
                  onClick={() => onNavigate(attachments[currentIdx - 1])}
                  className={BAR_BUTTON}
                  title={t('common.previous')}
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
              )}
              {hasNext && (
                <button
                  onClick={() => onNavigate(attachments[currentIdx + 1])}
                  className={BAR_BUTTON}
                  title={t('common.next')}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={async () => {
                  try {
                    const res = await fetch(url);
                    await saveFile(await res.blob(), filename || 'download');
                  } catch {
                    openExternal(url);
                  }
                }}
                className={BAR_BUTTON}
                title={t('common.download')}
              >
                <Download className="w-4 h-4" />
              </button>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className={BAR_BUTTON}
                title={t('ui_common.attachment.open_in_browser')}
              >
                <ExternalLink className="w-4 h-4" />
              </a>
              <button
                onClick={onClose}
                className={BAR_BUTTON}
                title={t('common.close')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* ── Preview area ──────────────────────────────────────── */}
          <div className="flex-1 overflow-auto flex items-center justify-center p-4 relative">
            {hasPrev && (
              <button
                onClick={() => onNavigate(attachments[currentIdx - 1])}
                className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/80 text-white/90 rounded-full z-10"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}
            {hasNext && (
              <button
                onClick={() => onNavigate(attachments[currentIdx + 1])}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/80 text-white/90 rounded-full z-10"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            )}

            {isImage ? (
              <img
                src={url}
                alt={filename}
                className="max-w-full max-h-full object-contain"
              />
            ) : isPdf ? (
              <iframe
                src={url}
                title={filename}
                className="w-full h-full rounded border-0"
                style={{ minHeight: '70vh' }}
              />
            ) : isOffice ? (
              <iframe
                src={googleUrl}
                title={filename}
                className="w-full h-full rounded border-0"
                style={{ minHeight: '70vh' }}
              />
            ) : (
              <div className="text-center text-white/60 space-y-4">
                <p className="text-sm">{t('ui_common.attachment.preview_unavailable')}</p>
                <a
                  href={url}
                  download={filename}
                  className="inline-block px-4 py-2 bg-amber-500 text-black rounded-lg font-medium text-sm"
                >
                  {t('common.download')}
                </a>
              </div>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
